// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import { FinancialEntryRow } from '@/components/financial/FinancialEntryRow';
import { isClientCourtCost } from '@/utils/courtCosts';
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, DollarSign, BarChart3, TrendingUp, Sparkles, Brain, TrendingDown, Repeat, Calendar, Target, CheckCircle, Clock, ChevronDown, ChevronRight, Scale, FileText, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  convertLocalDateToUTC,
  convertUTCToBrazilianDate,
  checkOverdueStatus,
  formatCreationDate,
  parseLocalYmdToDate,
} from "@/utils/dateUtils";
import { groupInstallments, createConsolidatedEntry, isInstallmentEntry, getMainInstallmentEntry } from "@/utils/installmentUtils";
import { groupByMonth, sortEntriesByPaymentProximity, formatMonthLabel } from "@/utils/monthlyUtils";
import {
  entryCountsInOperationalFinance,
  POR_EVENTO_CONDITIONAL_TYPE,
} from "@/utils/financialOperationalImpact";
import FinancialKPIs from "@/components/financial/FinancialKPIs";
import CashFlowChart from "@/components/financial/CashFlowChart";
import FinancialDashboard from "@/components/financial/FinancialDashboard";
import SmartFinancialEntryForm from "@/components/financial/SmartFinancialEntryForm";
import RecurringEntryForm from "@/components/financial/RecurringEntryForm";
import RecurringForecast from "@/components/financial/RecurringForecast";
import { ProcessesFinancialDashboard } from "@/components/financial/ProcessesFinancialDashboard";
import SmartPaymentAlerts from "@/components/financial/SmartPaymentAlerts";
import EnhancedPaymentAlerts from "@/components/financial/EnhancedPaymentAlerts";

import PendingPaymentAlerts from "@/components/financial/PendingPaymentAlerts";


interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  due_date?: string;
  status: string;
  created_at: string;
  client_id?: string;
  case_id?: string;
  administrative_process_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
  payment_confirmed?: boolean;
  is_negotiating?: boolean;
  is_installment?: boolean;
  installments_count?: number;
  installment_frequency?: string;
  down_payment?: number;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  actual_payment_date?: string;
  is_overdue?: boolean;
  overdue_days?: number;
  rescheduled_count?: number;
  last_reschedule_date?: string;
  payment_notes?: string;
}

interface RecurringEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  due_day: number; // Dia do mês para vencimento
  is_active: boolean;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface Client {
  id: string;
  name: string;
  email: string;
}

interface Case {
  id: string;
  title: string;
  case_number?: string;
}

interface ArchivedPayment {
  id: string;
  recurring_entry_id: string;
  payment_month: number;
  payment_year: number;
  payment_date: string;
  recurring_entries: {
    description: string;
    amount: number;
    entry_type: string;
    category: string;
    clients?: {
      name: string;
      email: string;
    };
    cases?: {
      title: string;
      case_number?: string;
    };
  };
}

/** Destaque visual na lista quando o lançamento já está pago. */
const financialEntryRowClassName = (status: string, compact?: boolean) => {
  const pad = compact
    ? "px-4 sm:px-6 py-2 sm:py-3 ml-4 border-l border-slate-200"
    : "px-5 sm:px-6 py-3.5";
  const accent = status === "Pago"
    ? "shadow-[inset_3px_0_0_0_rgb(16,185,129)]"
    : status === "Vencido"
      ? "shadow-[inset_3px_0_0_0_rgb(239,68,68)]"
      : "shadow-[inset_3px_0_0_0_rgb(196,171,131)]";
  return `${pad} ${accent} bg-white hover:bg-[#faf7f2]/60 transition-colors duration-200`;
};

const Financial = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<FinancialEntry[]>([]);
  const [recurringEntries, setRecurringEntries] = useState<RecurringEntry[]>([]);
  const [allEntriesWithRecurring, setAllEntriesWithRecurring] = useState<FinancialEntry[]>([]);
  const [archivedPayments, setArchivedPayments] = useState<ArchivedPayment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [administrativeProcesses, setAdministrativeProcesses] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRecurringDialogOpen, setIsRecurringDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<FinancialEntry | null>(null);
  const [editingRecurringEntry, setEditingRecurringEntry] = useState<RecurringEntry | null>(null);
  const [dialogType, setDialogType] = useState<"receita" | "despesa">("receita");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [conditionalFilter, setConditionalFilter] = useState("all");
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1); // getMonth() retorna 0-11
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [pendingView, setPendingView] = useState<"cobrancas" | "exito">("cobrancas");
  const [expandedInstallments, setExpandedInstallments] = useState<Set<string>>(new Set());
  const [isPaymentDateDialogOpen, setIsPaymentDateDialogOpen] = useState(false);
  const [selectedEntryForPayment, setSelectedEntryForPayment] = useState<FinancialEntry | null>(null);
  const [paymentDateForm, setPaymentDateForm] = useState({ paymentDate: '', notes: '' });


  const statusOptions = ["Pendente", "Pago", "Vencido", "Cancelado", "Em Negociação"];

  const fetchEntries = useCallback(async () => {
    try {
      // Buscar entradas financeiras com joins para clientes e casos
      const { data: entriesData, error } = await supabase
        .from('financial_entries')
        .select(`
          *,
          clients(name, email),
          cases(id, title, case_number)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Para entradas de processos administrativos, buscar informações adicionais
      const enrichedEntries = await Promise.all(
        (entriesData || []).map(async (entry) => {
          // Simular due_date baseado no payment_date para compatibilidade temporária
          const entryWithDueDate = {
            ...entry,
            due_date: entry.payment_date // Usar payment_date como due_date temporariamente
          };

          // Se tem administrative_process_id, buscar informações do processo administrativo
          if (entry.administrative_process_id) {
            // Buscar informações do processo administrativo
            const { data: adminProcess } = await supabase
              .from('administrative_processes')
              .select('title, client_id, clients(name, email)')
              .eq('id', entry.administrative_process_id)
              .single();
            
            if (adminProcess) {
              return {
                ...entryWithDueDate,
                cases: { title: adminProcess.title, case_number: null },
                clients: adminProcess.clients || entry.clients
              };
            }
          }
          // Se tem case_id mas não tem informações de cases (processo administrativo legado)
          else if (entry.case_id && !entry.cases && entry.description && entry.description.includes('[ADMIN]')) {
            // Buscar informações do processo administrativo
            const { data: adminProcess } = await supabase
              .from('administrative_processes')
              .select('title, client_id, clients(name, email)')
              .eq('id', entry.case_id)
              .single();
            
            if (adminProcess) {
              return {
                ...entryWithDueDate,
                cases: { title: adminProcess.title, case_number: null },
                clients: adminProcess.clients || entry.clients,
                administrative_process_id: entry.case_id
              };
            }
          }
          return entryWithDueDate;
        })
      );

      // Custas processuais pagas pelo cliente ficam só na tela do processo
      setEntries(enrichedEntries.filter((entry) => !isClientCourtCost(entry)));
    } catch (error) {
      console.error('Erro ao buscar entradas:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar entradas financeiras",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchRecurringEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('recurring_entries')
        .select(`
          *,
          clients(name, email),
          cases(id, title, case_number)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.log('Tabela recurring_entries ainda não existe:', error);
        setRecurringEntries([]);
        return;
      }
      setRecurringEntries(data || []);
    } catch (error) {
      console.error('Erro ao buscar entradas recorrentes:', error);
      setRecurringEntries([]);
    }
  };

  const fetchArchivedPayments = async () => {
    try {
      const { data, error } = await supabase
        .from('recurring_payments')
        .select(`
          *,
          recurring_entries(
            description,
            amount,
            entry_type,
            category,
            clients(name, email),
            cases(id, title, case_number)
          )
        `)
        .order('payment_date', { ascending: false });

      if (error) {
        console.log('Tabela recurring_payments ainda não existe:', error);
        setArchivedPayments([]);
        return;
      }
      setArchivedPayments(data || []);
    } catch (error) {
      console.error('Erro ao buscar pagamentos arquivados:', error);
      setArchivedPayments([]);
    }
  };

  // Função para gerar entradas virtuais baseadas nas recorrentes
  const generateVirtualEntries = useCallback(async () => {
    const virtualEntries: FinancialEntry[] = [];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    const currentYear = currentDate.getFullYear();

    // Buscar pagamentos já registrados para o mês atual
    const { data: existingPayments } = await supabase
      .from('recurring_payments')
      .select('recurring_entry_id')
      .eq('payment_month', currentMonth)
      .eq('payment_year', currentYear);

    const paidRecurringIds = new Set(existingPayments?.map(p => p.recurring_entry_id) || []);

    recurringEntries
      .filter(entry => entry.is_active && !paidRecurringIds.has(entry.id))
      .forEach(entry => {
        // Criar entrada virtual para o mês atual apenas se não foi paga
        const virtualEntry: FinancialEntry = {
          id: `virtual-${entry.id}-${currentMonth - 1}-${currentYear}`, // Ajustar para JavaScript months
          description: `${entry.description} (Recorrente)`,
          amount: entry.amount,
          entry_type: entry.entry_type,
          category: entry.category,
          status: 'Pendente', // Entradas recorrentes são consideradas pendentes até confirmadas
          payment_date: new Date(currentYear, currentMonth - 1, entry.due_day).toISOString().split('T')[0],
          created_at: new Date(currentYear, currentMonth - 1, entry.due_day).toISOString(),
          clients: entry.clients,
          cases: entry.cases,
          client_id: entry.client_id,
          case_id: entry.case_id
        };
        virtualEntries.push(virtualEntry);
      });

    return virtualEntries;
  }, [recurringEntries]);

  // Combinar entradas reais com virtuais para alertas
  const getAllEntriesWithRecurring = useCallback(async () => {
    const virtualEntries = await generateVirtualEntries();
    return [...entries, ...virtualEntries];
  }, [entries, generateVirtualEntries]);

  // Função para atualizar as entradas combinadas
  const updateAllEntriesWithRecurring = useCallback(async () => {
    const combinedEntries = await getAllEntriesWithRecurring();
    setAllEntriesWithRecurring(combinedEntries);
  }, [getAllEntriesWithRecurring]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const fetchCases = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('*')
        .order('title');

      if (error) throw error;
      setCases(data || []);
    } catch (error) {
      console.error('Erro ao buscar processos:', error);
    }
  };

  const fetchAdministrativeProcesses = async () => {
    try {
      const { data, error } = await supabase
        .from('administrative_processes')
        .select('id, title, client_name, process_number, fee_configuration')
        .order('title');

      if (error) throw error;
      setAdministrativeProcesses(data || []);
    } catch (error) {
      console.error('Erro ao buscar processos administrativos:', error);
    }
  };



  useEffect(() => {
    fetchEntries();
    fetchRecurringEntries();
    fetchArchivedPayments();
    fetchClients();
    fetchCases();
    fetchAdministrativeProcesses();
  }, [fetchEntries]);

  // Atualizar entradas combinadas quando entries ou recurringEntries mudarem
  useEffect(() => {
    if (entries.length > 0 || recurringEntries.length > 0) {
      updateAllEntriesWithRecurring();
    }
  }, [entries, recurringEntries, updateAllEntriesWithRecurring]);

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('financial_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setEntries(entries.filter(entry => entry.id !== id));
      toast({
        title: "Sucesso",
        description: "Entrada excluída com sucesso",
      });
    } catch (error) {
      console.error('Erro ao excluir entrada:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir entrada",
        variant: "destructive",
      });
    }
  };

  const handleDeleteRecurringEntry = async (id: string) => {
    try {
      const { error } = await supabase
        .from('recurring_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setRecurringEntries(recurringEntries.filter(entry => entry.id !== id));
      toast({
        title: "Sucesso",
        description: "Entrada recorrente excluída com sucesso",
      });
    } catch (error) {
      console.error('Erro ao excluir entrada recorrente:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir entrada recorrente",
        variant: "destructive",
      });
    }
  };

  const handleNewEntry = (type: "receita" | "despesa") => {
    setEditingEntry(null);
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const handleNewRecurringEntry = (type: "receita" | "despesa") => {
    setEditingRecurringEntry(null);
    setDialogType(type);
    setIsRecurringDialogOpen(true);
  };

  const handleDefinePaymentDate = (entry: FinancialEntry) => {
    setSelectedEntryForPayment(entry);
    setPaymentDateForm({ paymentDate: '', notes: '' });
    setIsPaymentDateDialogOpen(true);
  };

  const handleConfirmPaymentDate = async () => {
    if (!selectedEntryForPayment || !paymentDateForm.paymentDate) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const paymentDateIso = convertLocalDateToUTC(paymentDateForm.paymentDate);

      // Atualizar a entrada atual com a data de pagamento
      const { error: updateError } = await supabase
        .from('financial_entries')
        .update({
          payment_date: paymentDateIso,
          status: 'Pago',
          actual_payment_date: paymentDateIso,
          payment_notes: paymentDateForm.notes || 'Data de recebimento definida'
        })
        .eq('id', selectedEntryForPayment.id);

      if (updateError) throw updateError;

      // Se for um pagamento parcelado, criar as próximas parcelas automaticamente
      if (selectedEntryForPayment.is_installment && selectedEntryForPayment.installments_count && selectedEntryForPayment.installments_count > 1) {
        const baseDate = parseLocalYmdToDate(paymentDateForm.paymentDate.slice(0, 10));
        if (!baseDate) throw new Error('Data de pagamento inválida');
        const installmentsToCreate = [];
        const freq = selectedEntryForPayment.installment_frequency || 'mensal';

        for (let i = 1; i < selectedEntryForPayment.installments_count; i++) {
          const nextPaymentDate = new Date(
            baseDate.getFullYear(),
            baseDate.getMonth(),
            baseDate.getDate()
          );

          switch (freq) {
            case 'semanal':
              nextPaymentDate.setDate(nextPaymentDate.getDate() + i * 7);
              break;
            case 'quinzenal':
              nextPaymentDate.setDate(nextPaymentDate.getDate() + i * 15);
              break;
            case 'bimestral':
              nextPaymentDate.setMonth(baseDate.getMonth() + i * 2);
              break;
            case 'trimestral':
              nextPaymentDate.setMonth(baseDate.getMonth() + i * 3);
              break;
            case 'semestral':
              nextPaymentDate.setMonth(baseDate.getMonth() + i * 6);
              break;
            case 'anual':
              nextPaymentDate.setFullYear(baseDate.getFullYear() + i);
              break;
            case 'mensal':
            default:
              nextPaymentDate.setMonth(baseDate.getMonth() + i);
              break;
          }

          const y = nextPaymentDate.getFullYear();
          const mo = String(nextPaymentDate.getMonth() + 1).padStart(2, '0');
          const da = String(nextPaymentDate.getDate()).padStart(2, '0');
          const nextYmd = `${y}-${mo}-${da}`;

          installmentsToCreate.push({
            description: `${selectedEntryForPayment.description} - Parcela ${i + 1}/${selectedEntryForPayment.installments_count}`,
            amount: selectedEntryForPayment.amount,
            entry_type: selectedEntryForPayment.entry_type,
            category: selectedEntryForPayment.category,
            payment_date: convertLocalDateToUTC(nextYmd),
            status: 'Pendente',
            client_id: selectedEntryForPayment.client_id,
            case_id: selectedEntryForPayment.case_id,
            administrative_process_id: selectedEntryForPayment.administrative_process_id,
            is_installment: true,
            installments_count: selectedEntryForPayment.installments_count,
            installment_frequency: selectedEntryForPayment.installment_frequency,
            payment_notes: `Parcela ${i + 1} de ${selectedEntryForPayment.installments_count} - Agendada automaticamente`,
            user_id: user.id
          });
        }

        // Inserir todas as parcelas de uma vez
        if (installmentsToCreate.length > 0) {
          const { error: installmentsError } = await supabase
            .from('financial_entries')
            .insert(installmentsToCreate);

          if (installmentsError) throw installmentsError;
        }
      }

      // Atualizar as listas
      await fetchEntries();
      await updateAllEntriesWithRecurring();

      toast({
        title: "Sucesso",
        description: selectedEntryForPayment.is_installment && selectedEntryForPayment.installments_count && selectedEntryForPayment.installments_count > 1
          ? `Data de recebimento definida e ${selectedEntryForPayment.installments_count - 1} parcelas futuras agendadas automaticamente`
          : "Data de recebimento definida com sucesso",
      });

      setIsPaymentDateDialogOpen(false);
      setSelectedEntryForPayment(null);
    } catch (error) {
      console.error('Erro ao definir data de pagamento:', error);
      toast({
        title: "Erro",
        description: "Erro ao definir data de recebimento",
        variant: "destructive",
      });
    }
  };

  const handleConfirmRecurringPayment = async (recurringEntryId: string, description: string, amount: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();

      // Verificar se já existe um pagamento registrado para este mês
      const { data: existingPayment } = await supabase
        .from('recurring_payments')
        .select('id')
        .eq('recurring_entry_id', recurringEntryId)
        .eq('payment_month', currentMonth)
        .eq('payment_year', currentYear)
        .single();

      if (existingPayment) {
        toast({
          title: "Aviso",
          description: "Pagamento já foi confirmado para este mês",
          variant: "destructive"
        });
        return;
      }

      // Buscar dados completos da entrada recorrente
      const recurringEntry = recurringEntries.find(entry => entry.id === recurringEntryId);
      if (!recurringEntry) {
        throw new Error('Entrada recorrente não encontrada');
      }

      // Registrar o pagamento na tabela recurring_payments
      const { error: paymentError } = await supabase
        .from('recurring_payments')
        .insert({
          recurring_entry_id: recurringEntryId,
          payment_month: currentMonth,
          payment_year: currentYear,
          payment_date: currentDate.toISOString(),
          amount: recurringEntry.amount,
          user_id: user.id
        });

      if (paymentError) throw paymentError;

      // Criar entrada financeira real
      const { error: entryError } = await supabase
        .from('financial_entries')
        .insert({
          description: `${description} (Recorrente - ${currentMonth}/${currentYear})`,
          amount: amount,
          entry_type: recurringEntry.entry_type,
          category: recurringEntry.category,
          status: 'Pago',
          payment_date: currentDate.toISOString(),
          client_id: recurringEntry.client_id,
          case_id: recurringEntry.case_id,
          user_id: user.id
        });

      if (entryError) throw entryError;

      // Atualizar as listas
      await fetchEntries();
      await fetchArchivedPayments();
      await updateAllEntriesWithRecurring();

      toast({
        title: "Sucesso",
        description: `Pagamento de ${recurringEntry.entry_type} confirmado e registrado`,
      });
    } catch (error) {
      console.error('Erro ao confirmar pagamento recorrente:', error);
      toast({
        title: "Erro",
        description: "Erro ao confirmar pagamento recorrente",
        variant: "destructive",
      });
    }
  };



  // Agrupar parcelamentos
  const { installmentGroups, regularEntries } = groupInstallments(entries);
  
  // Filtrar entradas regulares
  const filteredRegularEntries = regularEntries.filter(entry => {
    const matchesSearch = entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.clients?.name && entry.clients.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entry.cases?.title && entry.cases.title.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = typeFilter === "all" || entry.entry_type === typeFilter;
    const matchesStatus = statusFilter === "all" || entry.status === statusFilter;
    const matchesConditional = conditionalFilter === "all" ||
      (conditionalFilter === "por_evento" && entry.is_conditional_payment && entry.conditional_type === POR_EVENTO_CONDITIONAL_TYPE) ||
      (conditionalFilter === "outras" && !(entry.is_conditional_payment && entry.conditional_type === POR_EVENTO_CONDITIONAL_TYPE));
    
    // Log para debug de honorários de êxito
    if (entry.category === 'Honorários de Êxito') {
      console.log('DEBUG - Entrada de Honorários de Êxito:', {
        id: entry.id,
        description: entry.description,
        status: entry.status,
        payment_date: entry.payment_date,
        amount: entry.amount
      });
    }
    
    // Lógica de exibição:
    // 1. Não exibir entradas condicionais sem data de pagamento (exceto honorários de êxito)
    // 2. Sempre exibir honorários de êxito com status 'Pendente' e data de pagamento
    // 3. Não exibir honorários de êxito sem data de pagamento (devem aparecer apenas nos post-its)
    
    let shouldShowInTab = true;
    
    if (entry.category === 'Honorários de Êxito') {
      // Para honorários de êxito: só mostrar se tiver data de pagamento
      shouldShowInTab = !!entry.payment_date;
    } else {
      // Para outras categorias: não mostrar se for condicional sem data
      shouldShowInTab = !(entry.status === 'Condicional' && !entry.payment_date);
    }
    
    const finalShouldShow = shouldShowInTab;
    
    // Log adicional para debug
    if (entry.category === 'Honorários de Êxito') {
      console.log('DEBUG - shouldShowInTab para Honorários de Êxito:', {
        id: entry.id,
        shouldShowInTab,
        finalShouldShow,
        matchesSearch,
        matchesType,
        matchesStatus
      });
    }
    
    return matchesSearch && matchesType && matchesStatus && matchesConditional && finalShouldShow;
  });

  // Filtrar grupos de parcelamentos
  const filteredInstallmentGroups = installmentGroups.filter(group => {
    const mainEntry = createConsolidatedEntry(group);
    const matchesSearch = mainEntry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mainEntry.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (mainEntry.clients?.name && mainEntry.clients.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (mainEntry.cases?.title && mainEntry.cases.title.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = typeFilter === "all" || mainEntry.entry_type === typeFilter;
    const matchesStatus = statusFilter === "all" || mainEntry.status === statusFilter;
    const matchesConditional = conditionalFilter === "all" ||
      (conditionalFilter === "por_evento" && mainEntry.is_conditional_payment && mainEntry.conditional_type === POR_EVENTO_CONDITIONAL_TYPE) ||
      (conditionalFilter === "outras" && !(mainEntry.is_conditional_payment && mainEntry.conditional_type === POR_EVENTO_CONDITIONAL_TYPE));
    
    // Aplicar a mesma lógica de exibição para parcelamentos
    let shouldShowInTab = true;
    
    if (mainEntry.category === 'Honorários de Êxito') {
      // Para honorários de êxito: só mostrar se tiver data de pagamento
      shouldShowInTab = !!mainEntry.payment_date;
    } else {
      // Para outras categorias: não mostrar se for condicional sem data
      shouldShowInTab = !(mainEntry.status === 'Condicional' && !mainEntry.payment_date);
    }
    
    return matchesSearch && matchesType && matchesStatus && matchesConditional && shouldShowInTab;
  });

  const handleEdit = (entry: FinancialEntry) => {
    // Linha consolidada de parcelamento usa descrição/valor agregados; editar deve usar o lançamento real do BD.
    let entryToEdit = entry;
    if (entry.description?.includes("pendentes)")) {
      const group = filteredInstallmentGroups.find(
        (g) => createConsolidatedEntry(g).id === entry.id
      );
      if (group) entryToEdit = getMainInstallmentEntry(group);
    }
    setEditingEntry(entryToEdit);
    setDialogType(entryToEdit.entry_type as "receita" | "despesa");
    setIsDialogOpen(true);
  };

  // Combinar entradas regulares com parcelamentos consolidados
  const allFilteredEntries = [
    ...filteredRegularEntries,
    ...filteredInstallmentGroups.map(group => createConsolidatedEntry(group))
  ];
  const sortedAllFilteredEntries = [...allFilteredEntries].sort(
    sortEntriesByPaymentProximity
  );

  // Por mês: usar cada parcela com sua própria data (parcelas futuras aparecem no mês correto).
  // A aba "Todos" continua com visão consolidada em allFilteredEntries.
  // Lançamentos sem data (ex.: êxito, que só sai no fim do processo) não entram em nenhum mês
  const entriesWithDate = [
    ...filteredRegularEntries,
    ...filteredInstallmentGroups.flatMap((g) => g.allInstallments),
  ];
  const entriesForMonthlyView = entriesWithDate.filter((e) => !!e.payment_date);
  // Aba Pendentes: êxito sem data de um lado; parcelas, diligências e demais do outro
  const isExitoSemData = (e: FinancialEntry) =>
    !e.payment_date && (e.status === 'Condicional' || e.category === 'Honorários de Êxito' || /[êe]xito/i.test(e.description || ''));
  const pendingEntries = entries.filter((e) => e.status !== 'Pago');
  const exitoEntries = pendingEntries
    .filter(isExitoSemData)
    .sort((a, b) => (b.amount || 0) - (a.amount || 0));
  // Do vencimento mais próximo ao mais distante; sem data por último
  const scheduledEntries = pendingEntries
    .filter((e) => !isExitoSemData(e))
    .sort((a, b) => {
      if (!a.payment_date) return b.payment_date ? 1 : 0;
      if (!b.payment_date) return -1;
      return String(a.payment_date).localeCompare(String(b.payment_date));
    });
  const visiblePending = pendingView === 'exito' ? exitoEntries : scheduledEntries;
  const pendingRevenue = visiblePending.filter((e) => e.entry_type === 'receita').reduce((sum, e) => sum + (e.amount || 0), 0);
  const pendingExpenses = visiblePending.filter((e) => e.entry_type === 'despesa').reduce((sum, e) => sum + (e.amount || 0), 0);
  const monthlyGroups = groupByMonth(entriesForMonthlyView);

  // APENAS PARA APRESENTAÇÃO: Filtrar meses do ano atual
  // Sempre os 12 meses do ano; meses sem lançamentos aparecem vazios
  const currentYearMonths = Array.from({ length: 12 }, (_, index) => {
    const key = `${currentYear}-${String(index + 1).padStart(2, '0')}`;
    return (
      monthlyGroups.find(group => group.month === key) || {
        month: key,
        monthLabel: formatMonthLabel(key),
        entries: [],
        summary: { totalRevenue: 0, totalExpenses: 0, netAmount: 0, pendingRevenue: 0, pendingExpenses: 0, paidRevenue: 0, paidExpenses: 0 },
      }
    );
  });
  const monthsWithEntries = currentYearMonths.filter(group => group.entries.length > 0).length;

  // Encontrar o mês atual nos dados disponíveis
  const getCurrentMonthTab = () => {
    const currentMonthName = new Date(currentYear, currentMonth - 1).toLocaleDateString('pt-BR', { month: 'long' });
    const currentMonthLabel = `${currentMonthName} de ${currentYear}`;
    
    // Procurar o mês atual nos dados
    const currentMonthGroup = currentYearMonths.find(group => 
      group.monthLabel.toLowerCase() === currentMonthLabel.toLowerCase()
    );
    
    return currentMonthGroup ? currentMonthGroup.month : "all";
  };

  // Obter anos disponíveis para navegação
  const availableYears = [...new Set([
    ...monthlyGroups.map(group => parseInt(group.monthLabel.split(' ').pop() || '2024')),
    new Date().getFullYear(),
    currentYear,
  ])].sort((a, b) => b - a);

  // Funções de navegação
  const goToPreviousYear = () => {
    const currentIndex = availableYears.indexOf(currentYear);
    if (currentIndex < availableYears.length - 1) {
      setCurrentYear(availableYears[currentIndex + 1]);
    }
  };

  const goToNextYear = () => {
    const currentIndex = availableYears.indexOf(currentYear);
    if (currentIndex > 0) {
      setCurrentYear(availableYears[currentIndex - 1]);
    }
  };

  // Função para navegar para o processo
  const handleNavigateToProcess = (entry: FinancialEntry) => {
    console.log('🔍 Navegando para processo:', entry.cases);
    if (entry.cases?.id) {
      console.log('✅ Navegando para:', `/processo/${entry.cases.id}`);
      navigate(`/processo/${entry.cases.id}`);
    } else {
      console.log('❌ ID do processo não encontrado:', entry.cases);
    }
  };

  // Atualizar mês atual quando mudar o ano
  useEffect(() => {
    const now = new Date();
    if (currentYear === now.getFullYear()) {
      setCurrentMonth(now.getMonth() + 1);
    } else {
      // Se mudou para outro ano, usar dezembro como padrão
      setCurrentMonth(12);
    }
  }, [currentYear]);


  const filteredRecurringEntries = recurringEntries.filter(entry => {
    const matchesSearch = entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.clients?.name && entry.clients.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entry.cases?.title && entry.cases.title.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = typeFilter === "all" || entry.entry_type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Gestão financeira</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Financeiro</h1>
            <p className="mt-1 text-sm text-slate-500">Receitas, despesas e fluxo de caixa do escritório</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button 
              size="sm"
              onClick={() => handleNewEntry("receita")}
              className="h-9 rounded-xl bg-slate-900 text-[13px] text-white shadow-sm hover:bg-slate-800"
            >
              <TrendingUp className="w-4 h-4 mr-1 sm:mr-2 text-emerald-300" />
              <span className="hidden sm:inline">Nova Receita</span>
              <span className="sm:hidden">Receita</span>
            </Button>
            
            <Button 
              size="sm"
              onClick={() => handleNewEntry("despesa")}
              variant="outline"
              className="h-9 rounded-xl border-slate-200 bg-white text-[13px] text-slate-700 shadow-none hover:bg-slate-50"
            >
              <TrendingDown className="w-4 h-4 mr-1 sm:mr-2 text-red-500" />
              <span className="hidden sm:inline">Nova Despesa</span>
              <span className="sm:hidden">Despesa</span>
            </Button>
          </div>
        </div>

        {/* Central de Pagamentos Inteligente */}
        <div className="mb-6">
          <SmartPaymentAlerts entries={allEntriesWithRecurring} onEntryUpdate={fetchEntries} />
        </div>

        {/* Alertas de Datas Pendentes - Post-it Style */}
        <PendingPaymentAlerts
          pendingPayments={[
            // Processos administrativos com datas pendentes
            ...(() => {
                             // Filtrar processos administrativos que têm benefitFirstPaymentDatePending = true
               const pendingProcesses = administrativeProcesses.filter(process => {
                 const config = process.fee_configuration;
                 return config && 
                        config.benefitFirstPaymentDatePending === true && 
                        config.hasSuccessFee === true &&
                        config.successFeeLost !== true &&
                        (config.benefitValue > 0 || (config.benefitValueType === 'undefined' && config.undefinedBenefitFirstInstallmentValue > 0));
               });
              
                             return pendingProcesses.map(process => {
                 const config = process.fee_configuration;
                 const benefitValue = config.benefitValue || 0;
                 const installmentCount = config.benefitInstallmentCount || 1;
                 const installmentValue = config.benefitInstallmentValue || (benefitValue / installmentCount);
                 const successFeeValue = config.successFeeValue || 0;
                 
                 let successFeeAmount = 0;
                 let additionalInfo = '';
                 
                 // Verificar se é benefício indefinido
                 if (config.benefitValueType === 'undefined' && config.undefinedBenefitFirstInstallmentValue > 0) {
                   const undefinedInstallmentCount = config.undefinedBenefitFirstInstallmentsCount || 3;
                   const undefinedInstallmentValue = config.undefinedBenefitFirstInstallmentValue;
                   
                   if (config.successFeeType === 'percentage_undefined_installments') {
                     const totalFirstInstallments = undefinedInstallmentValue * undefinedInstallmentCount;
                     successFeeAmount = (totalFirstInstallments * successFeeValue) / 100;
                     additionalInfo = `Benefício Indefinido - ${undefinedInstallmentCount}x de R$ ${undefinedInstallmentValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | `;
                     additionalInfo += `Total Primeiras Parcelas: R$ ${totalFirstInstallments.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | `;
                     additionalInfo += `Hon. Êxito: R$ ${successFeeAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${successFeeValue}%)`;
                   } else {
                     successFeeAmount = successFeeValue;
                     additionalInfo = `Benefício Indefinido - ${undefinedInstallmentCount}x de R$ ${undefinedInstallmentValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | `;
                     additionalInfo += `Hon. Êxito: R$ ${successFeeAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (valor fixo)`;
                   }
                 } else if (benefitValue > 0 && successFeeValue > 0) {
                   // Calcular o valor correto dos honorários de êxito
                   if (config.successFeeType === 'percentage_global') {
                     successFeeAmount = (benefitValue * successFeeValue) / 100;
                     additionalInfo = `Benefício Total: R$ ${benefitValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | `;
                     additionalInfo += `Hon. Êxito Total: R$ ${successFeeAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${successFeeValue}%)`;
                   } else if (config.successFeeType === 'percentage_installment') {
                     const feePerInstallment = (installmentValue * successFeeValue) / 100;
                     successFeeAmount = feePerInstallment * installmentCount;
                     additionalInfo = `${installmentCount}x de R$ ${installmentValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | `;
                     additionalInfo += `Hon. Êxito: R$ ${feePerInstallment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} por parcela (${successFeeValue}%)`;
                   } else if (config.successFeeType === 'fixed_global') {
                     successFeeAmount = successFeeValue;
                     additionalInfo = `Benefício Total: R$ ${benefitValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | `;
                     additionalInfo += `Hon. Êxito: R$ ${successFeeAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (valor fixo)`;
                   } else if (config.successFeeType === 'fixed_monthly') {
                     successFeeAmount = successFeeValue * installmentCount;
                     additionalInfo = `${installmentCount}x de R$ ${installmentValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | `;
                     additionalInfo += `Hon. Êxito: R$ ${successFeeValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} por parcela (valor fixo)`;
                   }
                 }
                
                return {
                  id: process.id,
                  processNumber: process.process_number || process.id,
                  clientName: process.client_name || 'Cliente não informado',
                  paymentType: 'success_fee' as const,
                  amount: successFeeAmount,
                  dueDate: null, // Data pendente = null
                  description: `Honorários de Êxito - ${process.title} | ${additionalInfo}`,
                  priority: 'high' as 'high' | 'medium' | 'low',
                  additionalInfo,
                  administrativeProcessId: process.id
                };
              });
            })()
          ]}
          onProcessUpdate={() => {
            // Recarregar dados quando o processo for atualizado
            fetchEntries();
            fetchAdministrativeProcesses();
          }}
          onMarkAsPaid={(paymentId) => {
            // Lógica para entradas financeiras de honorários de êxito
            const entry = allEntriesWithRecurring.find(e => e.id === paymentId);
            if (entry) {
              handleDefinePaymentDate(entry);
            }
          }}
          onViewDetails={(paymentId) => {
            // Implementar lógica para ver detalhes do pagamento
            console.log('Ver detalhes:', paymentId);
          }}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <TabsList className="grid h-auto grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-4 lg:grid-cols-8">
            <TabsTrigger 
              value="overview" 
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Visão Geral</span>
              <span className="sm:hidden">Geral</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="entries" 
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Receitas & Despesas</span>
              <span className="sm:hidden">R&D</span>
            </TabsTrigger>

            <TabsTrigger
              value="undated"
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <Clock className="h-4 w-4" />
              <span>Pendentes</span>
              {pendingEntries.length > 0 && (
                <span className="rounded-full bg-[#c4ab83]/25 px-1.5 text-[10px] font-semibold tabular-nums text-[#7a6440]">{pendingEntries.length}</span>
              )}
            </TabsTrigger>
            
            <TabsTrigger 
              value="recurring" 
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <Repeat className="h-4 w-4" />
              <span className="hidden sm:inline">Recorrentes</span>
              <span className="sm:hidden">Recorrentes</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="intelligence" 
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <Brain className="h-4 w-4" />
              <span className="hidden sm:inline">Inteligência</span>
              <span className="sm:hidden">IA</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="kpis" 
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <Target className="h-4 w-4" />
              <span className="hidden sm:inline">KPIs</span>
              <span className="sm:hidden">KPIs</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="charts" 
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Gráficos</span>
              <span className="sm:hidden">Gráficos</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="processes" 
              className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm"
            >
              <Scale className="h-4 w-4" />
              <span className="hidden sm:inline">Processos</span>
              <span className="sm:hidden">Processos</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 sm:mt-6">
            <FinancialDashboard entries={entries} recurringEntries={recurringEntries} />
          </TabsContent>

          <TabsContent value="entries" className="mt-4 sm:mt-6">
            {/* Filters */}
            <div className="sticky top-2 z-20 mb-4 rounded-2xl border border-slate-200/80 bg-white/85 p-2 shadow-sm backdrop-blur-md sm:mb-5">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Buscar receitas, despesas, clientes ou processos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-10 rounded-xl border-slate-200 text-sm shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                </div>
                
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-10 rounded-xl border-slate-200 shadow-none sm:w-40">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Transações</SelectItem>
                    <SelectItem value="receita">Apenas Receitas</SelectItem>
                    <SelectItem value="despesa">Apenas Despesas</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-10 rounded-xl border-slate-200 shadow-none sm:w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {statusOptions.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={conditionalFilter} onValueChange={setConditionalFilter}>
                  <SelectTrigger className="h-10 rounded-xl border-slate-200 shadow-none sm:w-44">
                    <SelectValue placeholder="Origem" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Origens</SelectItem>
                    <SelectItem value="por_evento">Por evento</SelectItem>
                    <SelectItem value="outras">Outras pendentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Monthly Tabs */}
            <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200/80 bg-white sm:mb-6">
              <div className="relative flex flex-col gap-3 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-5 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-6">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Lançamentos</p>
                  <h3 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Receitas e despesas</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {allFilteredEntries.length} {allFilteredEntries.length === 1 ? 'transação encontrada' : 'transações encontradas'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleNewEntry("receita")}
                    className="h-9 rounded-xl bg-slate-900 text-[13px] text-white shadow-sm hover:bg-slate-800"
                  >
                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-emerald-300" />
                    <span className="hidden sm:inline">Nova Receita</span>
                    <span className="sm:hidden">Receita</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleNewEntry("despesa")}
                    variant="outline"
                    className="h-9 rounded-xl border-slate-200 bg-white text-[13px] text-slate-700 shadow-none hover:bg-slate-50"
                  >
                    <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-red-500" />
                    <span className="hidden sm:inline">Nova Despesa</span>
                    <span className="sm:hidden">Despesa</span>
                  </Button>
                </div>
              </div>
              
              <Tabs defaultValue={getCurrentMonthTab()} className="w-full">
                {/* Navegação por Ano */}
                <div className="border-b border-slate-100 bg-white px-5 py-3 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h4 className="text-sm font-medium text-slate-500">
                        Ano <span className="ml-1 text-lg font-semibold tabular-nums text-slate-900">{currentYear}</span>
                      </h4>
                      <span className="text-xs text-slate-400">
                        {monthsWithEntries} {monthsWithEntries === 1 ? 'mês com lançamentos' : 'meses com lançamentos'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPreviousYear}
                        disabled={availableYears.indexOf(currentYear) >= availableYears.length - 1}
                        className="h-8 rounded-lg border-slate-200 px-3 text-xs text-slate-600 shadow-none"
                      >
                        ← {availableYears[availableYears.indexOf(currentYear) + 1] || 'Anterior'}
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextYear}
                        disabled={availableYears.indexOf(currentYear) <= 0}
                        className="h-8 rounded-lg border-slate-200 px-3 text-xs text-slate-600 shadow-none"
                      >
                        {availableYears[availableYears.indexOf(currentYear) - 1] || 'Próximo'} →
                      </Button>
                    </div>
                  </div>
                </div>

                <TabsList className="grid h-auto grid-cols-3 gap-1 rounded-none border-b border-slate-100 bg-slate-50 p-2 sm:grid-cols-7 lg:grid-cols-[repeat(13,minmax(0,1fr))]">
                  <TabsTrigger value="all" className="flex items-center justify-center rounded-lg px-2 py-1.5 text-xs capitalize text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm">
                                        <span className="hidden sm:inline">Todos</span>
                    <span className="sm:hidden">Todos</span>
                  </TabsTrigger>
                  {currentYearMonths.map((monthGroup) => (
                    <TabsTrigger key={monthGroup.month} value={monthGroup.month} className="flex items-center justify-center rounded-lg px-2 py-1.5 text-xs capitalize text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm sm:text-sm">
                                            <span className="hidden sm:inline">{monthGroup.monthLabel.split(' ')[0]}</span>
                      <span className="sm:hidden">{monthGroup.monthLabel.split(' ')[0].substring(0, 3)}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="all" className="p-0">
                  <div className="divide-y divide-slate-100">
                    {sortedAllFilteredEntries.map((entry) => {
                      // Verificar se é um parcelamento consolidado
                      const isConsolidatedInstallment = entry.description.includes('pendentes)');
                      const installmentKey = isConsolidatedInstallment ? 
                        entry.description.replace(/\(.*?pendentes\)/, '').trim() : null;
                      const isExpanded = expandedInstallments.has(installmentKey || '');
                      
                      return (
                        <div key={entry.id} className={financialEntryRowClassName(entry.status)}>
                          <FinancialEntryRow
                            entry={entry}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onOpenProcess={handleNavigateToProcess}
                            expandable={isConsolidatedInstallment}
                            expanded={isExpanded}
                            onToggleExpand={() => {
                              const newExpanded = new Set(expandedInstallments);
                              if (isExpanded) {
                                newExpanded.delete(installmentKey || '');
                              } else {
                                newExpanded.add(installmentKey || '');
                              }
                              setExpandedInstallments(newExpanded);
                            }}
                          />

                          {/* Parcelas expandidas */}
                          {isConsolidatedInstallment && isExpanded && (
                            <div className="bg-gray-50 border-l-4 border-blue-200">
                              {filteredInstallmentGroups
                                .find(group => createConsolidatedEntry(group).id === entry.id)
                                ?.allInstallments
                                .filter(installment => installment.id !== entry.id)
                                .map(installment => (
                                  <div key={installment.id} className={financialEntryRowClassName(installment.status, true)}>
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <div className={`w-2 h-2 rounded-full ${installment.entry_type === 'receita' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                          <span className="text-sm font-medium text-gray-700">{installment.description}</span>
                                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            installment.status === 'Pago' ? 'bg-green-100 text-green-800' :
                                            installment.status === 'Pendente' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {installment.status}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-600">
                                          <span className="font-medium">R$ {installment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                          {installment.payment_date && (
                                            <span>Vencimento: {convertUTCToBrazilianDate(installment.payment_date)}</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleEdit(installment)}
                                          className="h-6 w-6 p-0 border-gray-300 text-gray-700 hover:bg-gray-50"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                {currentYearMonths.map((monthGroup) => (
                  <TabsContent key={monthGroup.month} value={monthGroup.month} className="p-0">
                    {/* Resumo do mês */}
                    <div className="border-b border-slate-100 bg-white px-5 py-4 sm:px-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h3 className="text-xl font-semibold capitalize tracking-tight text-slate-900">{monthGroup.monthLabel}</h3>
                        <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 text-sm">
                          <div className="flex flex-col gap-0.5 bg-white px-4 py-3">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Receitas</span>
                            <span className="text-lg font-semibold tabular-nums text-emerald-600">
                              R$ {monthGroup.summary.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                            {monthGroup.summary.pendingRevenue > 0 && (
                              <span className="text-xs text-orange-600 font-medium">
                                R$ {monthGroup.summary.pendingRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} pendentes
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col gap-0.5 bg-white px-4 py-3">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Despesas</span>
                            <span className="text-lg font-semibold tabular-nums text-red-600">
                              R$ {monthGroup.summary.totalExpenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                            {monthGroup.summary.pendingExpenses > 0 && (
                              <span className="text-xs text-orange-600 font-medium">
                                R$ {monthGroup.summary.pendingExpenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} pendentes
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col gap-0.5 bg-white px-4 py-3">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Saldo</span>
                            <span className={`text-lg font-bold tabular-nums ${
                              monthGroup.summary.netAmount === 0 ? 'text-slate-400' : monthGroup.summary.netAmount > 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {monthGroup.summary.netAmount > 0 ? '+' : ''}R$ {monthGroup.summary.netAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                            {(monthGroup.summary.pendingRevenue > 0 || monthGroup.summary.pendingExpenses > 0) && (
                              <span className="text-xs font-medium tabular-nums text-slate-500">
                                Previsto: R$ {(monthGroup.summary.totalRevenue - monthGroup.summary.totalExpenses).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Lista de entradas do mês */}
                    {monthGroup.entries.length === 0 && (
                      <div className="px-6 py-12 text-center">
                        <p className="text-sm font-medium text-slate-500">Nenhum lançamento</p>
                        <p className="mt-1 text-xs text-slate-400">Não há receitas ou despesas neste mês.</p>
                      </div>
                    )}
                    <div className="divide-y divide-slate-100">
                      {monthGroup.entries.map((entry) => {
                        // Verificar se é um parcelamento consolidado
                        const isConsolidatedInstallment = entry.description.includes('pendentes)');
                        const installmentKey = isConsolidatedInstallment ? 
                          entry.description.replace(/\(.*?pendentes\)/, '').trim() : null;
                        const isExpanded = expandedInstallments.has(installmentKey || '');
                        
                        return (
                          <div key={entry.id} className={financialEntryRowClassName(entry.status)}>
                            <FinancialEntryRow
                              entry={entry}
                              onEdit={handleEdit}
                              onDelete={handleDelete}
                              onOpenProcess={handleNavigateToProcess}
                              expandable={isConsolidatedInstallment}
                              expanded={isExpanded}
                              onToggleExpand={() => {
                                const newExpanded = new Set(expandedInstallments);
                                if (isExpanded) {
                                  newExpanded.delete(installmentKey || '');
                                } else {
                                  newExpanded.add(installmentKey || '');
                                }
                                setExpandedInstallments(newExpanded);
                              }}
                            />

                            {/* Parcelas expandidas */}
                            {isConsolidatedInstallment && isExpanded && (
                              <div className="bg-gray-50 border-l-4 border-blue-200">
                                {filteredInstallmentGroups
                                  .find(group => createConsolidatedEntry(group).id === entry.id)
                                  ?.allInstallments
                                  .filter(installment => installment.id !== entry.id)
                                  .map(installment => (
                                    <div key={installment.id} className={financialEntryRowClassName(installment.status, true)}>
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <div className={`w-2 h-2 rounded-full ${installment.entry_type === 'receita' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                            <span className="text-sm font-medium text-gray-700">{installment.description}</span>
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                              installment.status === 'Pago' ? 'bg-green-100 text-green-800' :
                                              installment.status === 'Pendente' ? 'bg-yellow-100 text-yellow-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {installment.status}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-4 text-xs text-gray-600">
                                            <span className="font-medium">R$ {installment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                            {installment.payment_date && (
                                              <span>Vencimento: {convertUTCToBrazilianDate(installment.payment_date)}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleEdit(installment)}
                                            className="h-6 w-6 p-0 border-gray-300 text-gray-700 hover:bg-gray-50"
                                          >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </div>

            {allFilteredEntries.length === 0 && (
              <div className="text-center py-8 sm:py-12 rounded-xl bg-card border border-border shadow-sm">
                <DollarSign className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-lg sm:text-xl font-semibold text-foreground">Nenhuma entrada encontrada</h3>
                <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                  {searchTerm || typeFilter !== "all" || statusFilter !== "all" ? "Tente ajustar seus filtros ou crie uma nova entrada." : "Comece criando sua primeira entrada financeira para ver os dados aqui."}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="undated" className="mt-4 sm:mt-6">
          <div className="mb-3 inline-flex rounded-xl bg-slate-100 p-1">
            {([
              { key: 'cobrancas', label: 'Parcelas e cobranças', count: scheduledEntries.length },
              { key: 'exito', label: 'Êxito', count: exitoEntries.length },
            ] as const).map((view) => (
              <button
                key={view.key}
                type="button"
                onClick={() => setPendingView(view.key)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-all duration-200 sm:text-sm ${
                  pendingView === view.key ? 'bg-white font-semibold text-[#7a6440] shadow-sm' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {view.label}
                <span className="rounded-full bg-[#c4ab83]/25 px-1.5 text-[10px] font-semibold tabular-nums text-[#7a6440]">{view.count}</span>
              </button>
            ))}
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            <div className="border-b border-slate-100 bg-[#faf7f2]/60 px-5 py-4 sm:px-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                    {pendingView === 'exito' ? 'Honorários de êxito' : 'Parcelas e cobranças'}
                  </h3>
                  <p className="mt-0.5 max-w-xl text-sm text-slate-500">
                    {pendingView === 'exito'
                      ? 'Valores que dependem do resultado do processo. Não entram em nenhum mês até você definir a data de pagamento.'
                      : 'Parcelas, diligências e demais lançamentos em aberto, do vencimento mais próximo ao mais distante.'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 text-sm">
                  <div className="flex flex-col gap-0.5 bg-white px-4 py-3">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">A receber</span>
                    <span className={`text-lg font-semibold tabular-nums ${pendingRevenue ? 'text-emerald-600' : 'text-slate-300'}`}>
                      R$ {pendingRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5 bg-white px-4 py-3">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">A pagar</span>
                    <span className={`text-lg font-semibold tabular-nums ${pendingExpenses ? 'text-red-600' : 'text-slate-300'}`}>
                      R$ {pendingExpenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {visiblePending.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="text-sm font-medium text-slate-500">
                  {pendingView === 'exito' ? 'Nenhum êxito pendente' : 'Nenhuma parcela ou cobrança em aberto'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {visiblePending.map((entry) => (
                  <div key={entry.id} className={financialEntryRowClassName(entry.status)}>
                    <FinancialEntryRow
                      entry={entry}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onOpenProcess={handleNavigateToProcess}
                    />
                  </div>
                ))}
              </div>
            )}
            </div>
          </TabsContent>

          <TabsContent value="recurring" className="mt-4 sm:mt-6">
            {/* Recorrentes: cabeçalho */}
            <div className="mb-5 overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
              <div className="relative flex flex-col gap-4 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-5 py-6 sm:flex-row sm:items-end sm:justify-between sm:px-6">
                <img
                  src="/imgs/logos allana/letra_A_logo.png"
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-3 top-1/2 h-32 w-32 -translate-y-1/2 select-none object-contain opacity-[0.12]"
                />
                <div className="relative flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#c4ab83]/20 text-[#9a7f52]">
                    <Repeat className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Recorrentes</p>
                    <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Receitas e despesas fixas</p>
                    <p className="mt-1 text-sm text-slate-500">Valores que se repetem todo mês, como aluguel, internet ou honorários mensais.</p>
                  </div>
                </div>
                <div className="relative flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleNewRecurringEntry("receita")}
                    className="h-9 rounded-xl bg-slate-900 text-[13px] text-white shadow-sm hover:bg-slate-800"
                  >
                    <TrendingUp className="mr-1.5 h-4 w-4 text-emerald-300" />
                    Nova receita fixa
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleNewRecurringEntry("despesa")}
                    className="h-9 rounded-xl border-slate-200 bg-white text-[13px] text-slate-700 shadow-none hover:bg-slate-50"
                  >
                    <TrendingDown className="mr-1.5 h-4 w-4 text-red-500" />
                    Nova despesa fixa
                  </Button>
                </div>
              </div>

              {/* Lista de recorrentes */}
              {filteredRecurringEntries.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm font-medium text-slate-500">Nenhuma receita ou despesa fixa cadastrada</p>
                  <p className="mt-1 text-xs text-slate-400">Cadastre aqui os valores que se repetem todo mês.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredRecurringEntries.map((entry) => {
                    const isRevenue = entry.entry_type === 'receita';
                    return (
                      <div
                        key={entry.id}
                        className={`flex items-start gap-4 px-5 py-3.5 transition-colors hover:bg-[#faf7f2]/60 sm:px-6 ${
                          isRevenue ? 'shadow-[inset_3px_0_0_0_rgb(16,185,129)]' : 'shadow-[inset_3px_0_0_0_rgb(239,68,68)]'
                        } ${entry.is_active ? '' : 'opacity-60'}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <h4 className="min-w-0 truncate text-sm font-medium text-slate-900 sm:text-[15px]">{entry.description}</h4>
                            {entry.category && (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{entry.category}</span>
                            )}
                            {!entry.is_active && (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">Inativa</span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {isRevenue ? 'Recebe' : 'Vence'} todo dia {entry.due_day}
                            </span>
                            {entry.clients?.name && <span className="font-medium text-slate-700">{entry.clients.name}</span>}
                            {entry.cases?.title && (
                              <button
                                type="button"
                                onClick={() => handleNavigateToProcess(entry)}
                                className="inline-flex min-w-0 items-center gap-1 text-[#7a6440] hover:underline"
                              >
                                <FileText className="h-3 w-3 shrink-0" />
                                <span className="truncate">{entry.cases.title}</span>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <span className={`whitespace-nowrap text-base font-semibold tabular-nums ${isRevenue ? 'text-emerald-600' : 'text-red-600'}`}>
                            {isRevenue ? '+' : '−'} R$ {entry.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            <span className="ml-0.5 text-xs font-normal text-slate-400">/mês</span>
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleConfirmRecurringPayment(entry.id, entry.description, entry.amount)}
                            className="h-8 rounded-lg border-emerald-200 px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                          >
                            <CheckCircle className="mr-1 h-3.5 w-3.5" />
                            {isRevenue ? 'Receber' : 'Pagar'}
                          </Button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingRecurringEntry(entry);
                              setDialogType(entry.entry_type as "receita" | "despesa");
                              setIsRecurringDialogOpen(true);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRecurringEntry(entry.id)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagamentos de recorrentes já confirmados */}
            {archivedPayments.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
                <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Histórico</p>
                    <p className="mt-1 text-xl font-semibold tracking-tight text-slate-900">Pagamentos confirmados</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {archivedPayments.length} {archivedPayments.length === 1 ? 'pagamento' : 'pagamentos'}
                    </p>
                    <p className="text-lg font-semibold tabular-nums text-slate-900">
                      R$ {archivedPayments.reduce((total, payment) => total + payment.recurring_entries.amount, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                {(() => {
                  const groupedByCategory = archivedPayments.reduce((acc, payment) => {
                    const category = payment.recurring_entries.category || 'Sem categoria';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(payment);
                    return acc;
                  }, {} as Record<string, typeof archivedPayments>);

                  return Object.entries(groupedByCategory).map(([category, payments]) => {
                    const categoryTotal = payments.reduce((total, payment) => total + payment.recurring_entries.amount, 0);
                    return (
                      <div key={category} className="border-b border-slate-100 last:border-b-0">
                        <div className="flex items-center justify-between bg-slate-50/70 px-5 py-2 sm:px-6">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            {category} · {payments.length} {payments.length === 1 ? 'item' : 'itens'}
                          </span>
                          <span className="text-sm font-semibold tabular-nums text-slate-700">
                            R$ {categoryTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {payments.map((payment) => {
                            const isRevenue = payment.recurring_entries.entry_type === 'receita';
                            return (
                              <div key={payment.id} className="flex items-center gap-4 px-5 py-3 sm:px-6">
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-slate-900">{payment.recurring_entries.description}</p>
                                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                                    <span>
                                      Referente a {String(payment.payment_month).padStart(2, '0')}/{payment.payment_year}
                                    </span>
                                    <span className="text-emerald-700">
                                      Pago em {new Date(payment.payment_date).toLocaleDateString('pt-BR')}
                                    </span>
                                    {payment.recurring_entries.clients?.name && (
                                      <span className="font-medium text-slate-700">{payment.recurring_entries.clients.name}</span>
                                    )}
                                  </p>
                                </div>
                                <span className={`whitespace-nowrap text-sm font-semibold tabular-nums ${isRevenue ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {isRevenue ? '+' : '−'} R$ {payment.recurring_entries.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </TabsContent>

          <TabsContent value="intelligence" className="mt-4 sm:mt-6">
            <RecurringForecast />
          </TabsContent>

          <TabsContent value="kpis" className="mt-4 sm:mt-6">
            <FinancialKPIs entries={entries} recurringEntries={recurringEntries} />
          </TabsContent>

          <TabsContent value="charts" className="mt-4 sm:mt-6">
            <CashFlowChart entries={entries} recurringEntries={recurringEntries} />
          </TabsContent>

          <TabsContent value="processes" className="mt-4 sm:mt-6">
            <div className="space-y-6">
              <ProcessesFinancialDashboard />
            </div>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <Dialog open={isPaymentDateDialogOpen} onOpenChange={setIsPaymentDateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Definir Data de Recebimento</DialogTitle>
              <DialogDescription>
                {selectedEntryForPayment?.is_installment && selectedEntryForPayment?.installments_count && selectedEntryForPayment.installments_count > 1
                  ? `Defina a data do primeiro recebimento. As próximas ${selectedEntryForPayment.installments_count - 1} parcelas serão agendadas automaticamente.`
                  : "Defina a data de recebimento para este pagamento."
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Data de Recebimento</label>
                <input
                  type="date"
                  value={paymentDateForm.paymentDate}
                  onChange={(e) => setPaymentDateForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Observações (opcional)</label>
                <textarea
                  value={paymentDateForm.notes}
                  onChange={(e) => setPaymentDateForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Adicione observações sobre este recebimento..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsPaymentDateDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmPaymentDate}
                disabled={!paymentDateForm.paymentDate}
                className="bg-green-600 hover:bg-green-700"
              >
                Confirmar Recebimento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>



        <SmartFinancialEntryForm
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingEntry(null);
          }}
          editingEntry={editingEntry}
          clients={clients}
          cases={cases}
          onSave={() => {
            setIsDialogOpen(false);
            setEditingEntry(null);
            fetchEntries();
          }}
          dialogType={dialogType}
        />

        <RecurringEntryForm
          isOpen={isRecurringDialogOpen}
          onClose={() => {
            setIsRecurringDialogOpen(false);
            setEditingRecurringEntry(null);
          }}
          editingEntry={editingRecurringEntry}
          clients={clients}
          cases={cases}
          onSave={() => {
            setIsRecurringDialogOpen(false);
            setEditingRecurringEntry(null);
            fetchRecurringEntries();
          }}
          dialogType={dialogType}
        />
      </div>
    </div>
  );
};

export default Financial;